import { useEffect } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { X, Waves, LogOut, Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/hooks/useAuth';
import { useSignOut } from '@/hooks/useSignOut';
import { useMembership, getRoleLabel } from '@/hooks/useMembership';
import { useFarms } from '@/hooks/useFarms';
import { getNavigationForRole } from '@/lib/navigation';
import { usePendingEditRequests } from '@/hooks/useEditRequestCounts';
import { FarmSwitcher } from './FarmSwitcher';
import { NavCountBadge } from './NavCountBadge';

interface MobileNavProps {
  open: boolean;
  onClose: () => void;
}

export function MobileNav({ open, onClose }: MobileNavProps) {
  const location = useLocation();
  const { user } = useAuth();
  const { signOut, signingOut } = useSignOut();
  const { role, membership } = useMembership();
  const { farms } = useFarms();

  const navigation = getNavigationForRole(role);
  const pendingEditRequests = usePendingEditRequests();

  // Prevent body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="relative z-[60] lg:hidden">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="pointer-events-none fixed inset-0 flex">
        <div className="pointer-events-auto relative mr-16 flex w-full max-w-xs flex-1">
          {/* Close button */}
          <div className="pointer-events-none absolute left-full top-0 flex w-16 justify-center pt-5">
            <button
              type="button"
              className="pointer-events-auto -m-2.5 p-2.5 text-white"
              onClick={onClose}
            >
              <span className="sr-only">Close sidebar</span>
              <X className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          {/* Sidebar content */}
          <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-5 pb-4">
            {/* Logo */}
            <div className="flex h-16 shrink-0 items-center">
              <Link to="/overview" onClick={onClose} className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary-600 flex items-center justify-center">
                  <Waves className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-gray-900">HiponHero</span>
              </Link>
            </div>

            {/* Farm: switcher when there is more than one to pick from, else the name */}
            {farms.length > 1 ? (
              <FarmSwitcher variant="drawer" onSelect={onClose} />
            ) : (
              membership?.farmName && (
                <div className="px-2 py-1.5 -mt-2">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {membership.farmName}
                  </p>
                </div>
              )
            )}

            {/* Navigation */}
            <nav className="flex flex-1 flex-col">
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul role="list" className="-mx-2 space-y-1">
                    {navigation.map((item) => {
                      const isActive = location.pathname.startsWith(item.href);
                      const pending = item.href === '/edit-requests' ? pendingEditRequests : 0;
                      return (
                        <li key={item.name}>
                          <Link
                            to={item.href}
                            onClick={onClose}
                            aria-label={pending > 0 ? `${item.name}, ${pending} pending` : undefined}
                            className={cn(
                              'group flex gap-x-3 rounded-lg p-3 text-base font-medium leading-6 transition-colors',
                              isActive
                                ? 'bg-primary-50 text-primary-700'
                                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                            )}
                          >
                            <item.icon
                              className={cn(
                                'h-6 w-6 shrink-0 transition-colors',
                                isActive
                                  ? 'text-primary-600'
                                  : 'text-gray-400 group-hover:text-gray-600'
                              )}
                            />
                            {item.name}
                            <NavCountBadge count={pending} />
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </li>

                {/* User section */}
                <li className="mt-auto">
                  <div className="rounded-lg p-3 bg-gray-50">
                    <div className="flex items-center gap-x-3">
                      <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                        <span className="text-primary-700 text-sm font-semibold">
                          {user?.email?.charAt(0).toUpperCase() || 'U'}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {user?.email || 'User'}
                        </p>
                        {role && (
                          <p className="text-xs text-gray-500">{getRoleLabel(role)}</p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => signOut()}
                      disabled={signingOut}
                      className="mt-3 flex min-h-12 w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
                    >
                      {signingOut ? (
                        <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
                      ) : (
                        <LogOut className="h-5 w-5 text-gray-500" />
                      )}
                      {signingOut ? 'Signing out…' : 'Sign out'}
                    </button>
                  </div>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}
